import { InfoCircleIcon, LinkBrokenBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Input, overlayStyle, Select, Stack, Switch, Tooltip, Uploader } from '@prophecy/ui-v3';
import React from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';

import { ProviderType } from '../../../../data/apis/types';
import { IdpProviderLabelMap } from '../../config/admin';
import ProphecyManaged, { SCIMField } from './ProphecyManaged';
import SamlSingleLogoutFields from './SamlSingleLogoutFields';
import { ScrollableStack } from './styled';
import { ComponentProps } from './types';

const StyledUploader = styled(Uploader)`
  margin: 0;
  padding: 0;
  > div {
    justify-content: flex-start;
  }
`;

export default function SamlAuth(props: ComponentProps) {
  const { control, errors, isSCIMV1 } = props;
  const uploadFiles = (files: Blob[], onChange: (e: unknown) => void) => {
    let reader = new FileReader();
    const uploadedFile = files[0];
    reader.readAsText(uploadedFile);
    reader.onloadend = () => {
      onChange(reader.result);
    };
  };
  return (
    <ScrollableStack gap={theme.spaces.x24}>
      {/*<Controller
        render={({ field }) => (
          <UserPrompt
            hasPrompt={!props.isSCIMEnabled || props.isSCIMV1}
            errorMsg={errors?.saml?.usernamePrompt?.message}
            value={field.value}
            onChange={field.onChange}
          />
        )}
        control={control}
        name='saml.usernamePrompt'
      />*/}
      {isSCIMV1 && <SCIMField {...props} isSAML />}
      <Controller
        render={({ field }) => (
          <Select
            label='IDP'
            {...field}
            required
            options={Object.keys(ProviderType).map((value) => ({ label: IdpProviderLabelMap?.[value], value }))}
            variant={errors.idp ? 'error' : 'primary'}
            helpText={errors.idp?.message}
            data-test-id='saml-auth-idp-select'
          />
        )}
        control={control}
        name='idp'
        rules={{
          required: 'IDP Provider is required'
        }}
      />
      {!isSCIMV1 && <ProphecyManaged {...props} isSAML />}
      <Controller
        render={({ field }) => (
          <Input
            label={
              <Stack gap={theme.spaces.x4} direction='horizontal'>
                SSO URL
                <Tooltip title='SSO URL generated by Okta.'>
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            }
            {...field}
            required
            variant={errors?.saml?.ssoURL ? 'error' : 'primary'}
            helpText={errors?.saml?.ssoURL?.message}
            data-test-id='saml-auth-sso-url-input'
          />
        )}
        control={control}
        name='saml.ssoURL'
        rules={{
          required: 'URL is required'
        }}
      />
      <Stack direction='horizontal' align='space-between' alignY='center'>
        <Controller
          render={({ field }) => (
            <StyledUploader showUploadList={true} maxFiles={1} onDrop={(files) => uploadFiles(files, field.onChange)}>
              <Stack gap={theme.spaces.x12} align='space-between' alignY='center' direction='horizontal'>
                <Button
                  icon={<LinkBrokenBIcon type='default' />}
                  variant='secondaryGray'
                  data-test-id='saml-auth-certificate-button'>
                  Certificate
                </Button>
                <Tooltip
                  overlayStyle={overlayStyle}
                  placement='right'
                  title='Certificate to use when validating the signature of the SAML response.'>
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            </StyledUploader>
          )}
          control={control}
          name='saml.caData'
        />
        <Controller
          render={({ field }) => (
            <Stack gap={theme.spaces.x4} direction='horizontal' alignY='center'>
              <Switch {...field} checked={Boolean(field.value)} data-test-id='saml-auth-skip-cert-verification-switch'>
                * Skip certificate verification
              </Switch>
              <Tooltip
                overlayStyle={overlayStyle}
                title='To skip signature validation, uncomment the following field. This should only be used during testing and may be removed in the future.'>
                <div>
                  <InfoCircleIcon type='default' color={theme.colors.gray500} />
                </div>
              </Tooltip>
            </Stack>
          )}
          control={control}
          name='saml.insecureSkipSignatureValidation'
        />
      </Stack>
      <Stack direction='horizontal' align='space-between' gap={theme.spaces.x12} width='100%'>
        <Stack width='50%'>
          <Controller
            render={({ field }) => (
              <Input
                label={
                  <Stack gap={theme.spaces.x4} direction='horizontal'>
                    * Entity issuer
                    <Tooltip
                      overlayStyle={overlayStyle}
                      title='A unique Okta Entity ID is generated for each application.'>
                      <div>
                        <InfoCircleIcon type='default' color={theme.colors.gray500} />
                      </div>
                    </Tooltip>
                  </Stack>
                }
                {...field}
                variant={errors?.saml?.entityIssuer ? 'error' : 'primary'}
                helpText={errors?.saml?.entityIssuer?.message}
                data-test-id='saml-auth-entity-issuer-input'
              />
            )}
            control={control}
            name='saml.entityIssuer'
            rules={{
              required: 'Entity Issuer is required'
            }}
          />
        </Stack>
        <Stack width='50%'>
          <Controller
            render={({ field }) => (
              <Input
                label={
                  <Stack gap={theme.spaces.x4} direction='horizontal'>
                    * SSO issuer
                    <Tooltip title='Issuer value expected in the SAML response.'>
                      <div>
                        <InfoCircleIcon type='default' color={theme.colors.gray500} />
                      </div>
                    </Tooltip>
                  </Stack>
                }
                {...field}
                variant={errors?.saml?.ssoIssuer ? 'error' : 'primary'}
                helpText={errors?.saml?.ssoIssuer?.message}
                data-test-id='saml-auth-sso-issuer-input'
              />
            )}
            control={control}
            name='saml.ssoIssuer'
            rules={{
              required: 'SSO Issuer is required'
            }}
          />
        </Stack>
      </Stack>
      <SamlSingleLogoutFields {...props} />
    </ScrollableStack>
  );
}
